import React, { useState, useRef, useCallback, useMemo } from 'react'
import { MessageList } from './MessageList'
import { ChatInput } from './ChatInput'
import { useChatWithSteps } from '../hooks/useChatWithSteps'
import { PARTICIPANT_COLORS, hashString } from './participantColors.js'
import { BotIcon, BackIcon, PeopleIcon } from './icons.jsx'
import './ChatInterface.css'

/**
 * ChatInterface — pure chat view (messages + input + participant drawer)
 *
 * Receives host + participants from AppLayout (resolved from conv_index).
 * All navigation and conversation state is managed by AppLayout.
 */
export const ChatInterface = ({
  api,
  onAuthError,
  peerService,
  conversationId,
  host,           // { roleId, type, displayName, botConfig?, online }
  participants,   // [{ roleId, mentionName, roleType, displayName, description }]
  contacts,
  emptyMessage = 'How can I help you today?',
  placeholder = 'Message MyAgent...',
  chatTitle = '',
  onRenameChat,
  onBack,
  onAddParticipant,
  onRemoveParticipant,
  onRefreshContacts,
  onConfigureBot,
}) => {
  const { messages, isThinking, isLoadingHistory, sendMessage, appendMessage } = useChatWithSteps({
    api,
    onAuthError,
    peerService,
    conversationId,
    host,
    participants,
  })

  const allParticipants = useMemo(() => [host, ...participants].filter(Boolean), [host, participants])

  // Track active conversation via ref for async task handler
  const activeConvIdRef = useRef(conversationId)
  activeConvIdRef.current = conversationId


  // Editable title
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const titleDraftRef = useRef('')
  const titleInputRef = useRef(null)

  const startEditTitle = useCallback(() => {
    setTitleDraft(chatTitle)
    titleDraftRef.current = chatTitle
    setEditingTitle(true)
    setTimeout(() => titleInputRef.current?.select(), 0)
  }, [chatTitle])

  const commitTitle = useCallback(() => {
    setEditingTitle(false)
    const trimmed = titleDraftRef.current.trim()
    if (trimmed && trimmed !== chatTitle) onRenameChat?.(trimmed)
  }, [chatTitle, onRenameChat])

  // Bot drawer (local UI state)
  const [showBotDrawer, setShowBotDrawer] = useState(false)

  const openBotDrawer = useCallback(() => {
    setShowBotDrawer(true)
    onRefreshContacts?.()
  }, [onRefreshContacts])

  return (
    <div className="chat-interface">
      <div className="chat-header">
        <div className="chat-header-content">
          <div className="chat-header-left">
            {onBack && (
              <button className="chat-back-button" onClick={onBack} title="Back">
                <BackIcon />
              </button>
            )}
            {editingTitle ? (
              <input
                ref={titleInputRef}
                className="chat-title chat-title-input"
                value={titleDraft}
                onChange={e => { titleDraftRef.current = e.target.value; setTitleDraft(e.target.value) }}
                onBlur={commitTitle}
                onKeyDown={e => { if (e.key === 'Enter') commitTitle(); if (e.key === 'Escape') setEditingTitle(false) }}
              />
            ) : (
              <h1 className="chat-title" onDoubleClick={startEditTitle} title="Double-click to rename">{chatTitle}</h1>
            )}
          </div>
          <div className="chat-header-right">
            <button className="bots-button" onClick={openBotDrawer} title="Participants">
              <PeopleIcon size={16} />
              {participants.length > 0 && <span className="bots-count">{participants.length + 1}</span>}
            </button>
          </div>
        </div>
      </div>

      <MessageList
        messages={messages}
        isThinking={isThinking}
        isLoadingHistory={isLoadingHistory}
        emptyMessage={emptyMessage}
        participants={allParticipants}
        contacts={contacts}
        hostName={host?.displayName || 'MyAgent'}
      />

      <ChatInput
        onSend={sendMessage}
        disabled={isThinking}
        placeholder={placeholder}
        participants={allParticipants}
      />

      {showBotDrawer && (
        <>
          <div className="bot-drawer-overlay" onClick={() => setShowBotDrawer(false)} />
          <div className="bot-drawer">
            <div className="bot-drawer-header">
              <h2 className="bot-drawer-title">Participants</h2>
              <button className="bot-drawer-close" onClick={() => setShowBotDrawer(false)}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="bot-drawer-content">
              {/* Host */}
              {host && (() => {
                const color = PARTICIPANT_COLORS[hashString(host.roleId) % PARTICIPANT_COLORS.length]
                return (
                  <div className="bot-section">
                    <div className="bot-section-title">Host</div>
                    <div className="bot-item">
                      <div className="bot-avatar" style={{ background: `linear-gradient(135deg, ${color}22, ${color}11)` }}>
                        <BotIcon color={color} />
                        <span className={`bot-status-dot ${host.online ? 'online' : 'offline'}`} />
                      </div>
                      <div className="bot-info">
                        <div className="bot-name">{host.displayName}</div>
                      </div>
                      <span className="bot-badge-host">Host</span>
                    </div>
                  </div>
                )
              })()}

              {/* Current participants */}
              {participants.length > 0 && (
                <div className="bot-section">
                  <div className="bot-section-title">In Chat</div>
                  {participants.map(p => {
                    const color = PARTICIPANT_COLORS[hashString(p.roleId) % PARTICIPANT_COLORS.length]
                    const contact = contacts.find(c => c.roleId === p.roleId)
                    const online = contact?.online ?? false
                    return (
                      <div key={p.roleId} className="bot-item">
                        <div className="bot-avatar" style={{ background: `linear-gradient(135deg, ${color}22, ${color}11)` }}>
                          <BotIcon color={color} />
                          <span className={`bot-status-dot ${online ? 'online' : 'offline'}`} />
                        </div>
                        <div className="bot-info">
                          <div className="bot-name">{p.displayName}</div>
                          {p.description && <div className="bot-description">{p.description}</div>}
                        </div>
                        {onConfigureBot && <button className="bot-action bot-action-config" onClick={() => onConfigureBot(p)} title="Configure">&#9881;</button>}
                        <button className="bot-action bot-action-remove" onClick={() => onRemoveParticipant(p.roleId)}>Remove</button>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Available to add */}
              {(() => {
                const inChat = new Set([host?.roleId, ...participants.map(p => p.roleId)])
                const available = contacts.filter(c => !inChat.has(c.roleId) && c.roleType !== 'host')
                if (available.length === 0) return null
                return (
                  <div className="bot-section">
                    <div className="bot-section-title">Available</div>
                    {available.map(c => {
                      const color = PARTICIPANT_COLORS[hashString(c.roleId) % PARTICIPANT_COLORS.length]
                      return (
                        <div key={c.roleId} className={`bot-item ${c.online ? 'bot-item-clickable' : 'bot-item-disabled'}`} onClick={c.online ? () => onAddParticipant(c) : undefined}>
                          <div className="bot-avatar" style={{ background: `linear-gradient(135deg, ${color}22, ${color}11)` }}>
                            <BotIcon color={c.online ? color : 'var(--text-tertiary)'} />
                            <span className={`bot-status-dot ${c.online ? 'online' : 'offline'}`} />
                          </div>
                          <div className="bot-info">
                            <div className="bot-name">{c.displayName}</div>
                            {c.description && <div className="bot-description">{c.description}</div>}
                          </div>
                          {c.online && <span className="bot-action bot-action-add">Add</span>}
                        </div>
                      )
                    })}
                  </div>
                )
              })()}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
